import { gql, useQuery } from '@apollo/client';
import { Table, TableBody, TableCell, TableContainer, TableRow, type Theme } from '@mui/material';
import { useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type { Asset } from '@/types/GraphqlSchema';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '../../../types';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'key_reserves_metrics_table';

const styles = {
  key: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontWeight: 'bold',
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
  },
  value: {
    fontFamily: (theme: Theme) => theme.fonts.inter,
    border: 'none',
    backgroundColor: 'background.paper',
    fontSize: 'clamp(6px, 1.4vw, 12px)',
    padding: 'clamp(1px, 0.3vw, 8px)',
    lineHeight: 1,
    textAlign: 'right',
  },
};

// GraphQL query
const QUERY = gql`
  query AssetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      reserve {
        formattedTotalReservesGasBcf
        formattedTotalReservesLiquidMmbbl
        formattedTotalAccumulatedMmboe
        formattedTotalRemainingReservesMmboe
        formattedTotalRemainingReservesLiquidsMmbbl
        formattedTotalRemainingReservesGasBcf
        formattedTotalRemainingReservesPercentage
        formattedGor
      }
    }
  }
`;

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset | null>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  // Extract asset_id from portfolio
  const assetId = portfolio?.assets?.[0];

  const { loading, error } = useQuery<{ assetV2: Asset | null }>(QUERY, {
    variables: {
      input: {
        id: assetId,
      },
    },
    skip: !assetId || !!dataFetched,
    fetchPolicy: 'network-only', // Force network request to get fresh data
    onCompleted: response => {
      if (response?.assetV2) {
        setDataFetched(response.assetV2);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: response.assetV2,
          });
        }
      }
    },
  });

  return {
    data: dataFetched,
    error,
    loading,
  };
};

const ComponentView = ({ data }: ComponentViewProps<Asset | null | undefined>) => {
  const displayData = {
    'Total Liquids (MMbbl)': data?.reserve?.formattedTotalReservesLiquidMmbbl ?? 0,
    'Total Gas (Bcf)': data?.reserve?.formattedTotalReservesGasBcf ?? 0,
    'Accumulated Production (MMboe)': data?.reserve?.formattedTotalAccumulatedMmboe ?? 0,
    'Remaining Reserves (MMboe)': data?.reserve?.formattedTotalRemainingReservesMmboe ?? 0,
    'Remaining Liquids (MMbbl)': data?.reserve?.formattedTotalRemainingReservesLiquidsMmbbl ?? 0,
    'Remaining Gas (Bcf)': data?.reserve?.formattedTotalRemainingReservesGasBcf ?? 0,
    '% Reserves Remaining': data?.reserve?.formattedTotalRemainingReservesPercentage ?? '-',
    'GOR (scf/bbl)': data?.reserve?.formattedGor ?? '-',
  };

  return (
    // Responsive font sizing based on container size
    <ComponentBox sx={{ fontSize: 'clamp(8px, 2vw, 14px)' }}>
      <ComponentTitle>Key Reserves Metrics</ComponentTitle>
      <TableContainer>
        <Table size='small'>
          <TableBody>
            {Object.entries(displayData).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={styles.key}>{key}</TableCell>
                <TableCell sx={styles.value}>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset | null>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const KeyReservesMetricsComponent: ComponentDefinition<Asset | null> = {
  id: COMPONENT_ID,
  name: 'Key Reserves Metrics',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
